/**
 * End-to-end private-note encryption.
 *
 * The "private note" on a check-in is encrypted **in the browser** with a key
 * derived from the family PIN + family_id. The server stores only ciphertext
 * and has no way to read it even if the database is breached or subpoenaed.
 *
 * Key derivation: PBKDF2(SHA-256, pin, salt=family_id, 210 000 iterations)
 * Encryption:    AES-GCM-256 with a 12-byte random IV per message
 * Storage:       String of the form `enc:v1:{iv_b64}:{ct_b64}` — versioned so
 *                we can rotate algorithms later.
 *
 * Trade-offs we've accepted:
 *   - The derived key sits in localStorage next to the JWT. Anyone with
 *     already-unlocked browser access can read private notes — but they can
 *     also already read everything else in the family workspace, so there is
 *     no additional exposure. The point of this encryption is to protect
 *     against **server-side** compromise, not a stolen unlocked phone.
 *   - On PIN rotation, existing encrypted notes can no longer be decrypted.
 *     We warn before rotating (see rotate-PIN flow) and re-encrypt the
 *     viewer's own notes; other members' private notes remain locked.
 */

const KEY_STORAGE_PREFIX = "famfeel_pnk_"; // private-note-key, per family_id
const PBKDF2_ITERATIONS = 210_000;

// --- base64 helpers ---------------------------------------------------------
function u8ToB64(bytes) {
  let s = "";
  for (let i = 0; i < bytes.byteLength; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}
function b64ToU8(b64) {
  const s = atob(b64);
  const u = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) u[i] = s.charCodeAt(i);
  return u;
}

// --- derivation + storage ---------------------------------------------------
async function deriveKey(pin, familyId) {
  if (!window.crypto?.subtle) throw new Error("WebCrypto unavailable");
  const enc = new TextEncoder();
  const material = await crypto.subtle.importKey(
    "raw",
    enc.encode(pin),
    "PBKDF2",
    false,
    ["deriveKey"]
  );
  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt: enc.encode(`famfeel-pn:${familyId}`),
      iterations: PBKDF2_ITERATIONS,
      hash: "SHA-256",
    },
    material,
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"]
  );
}

async function exportKey(cryptoKey) {
  const raw = await crypto.subtle.exportKey("raw", cryptoKey);
  return u8ToB64(new Uint8Array(raw));
}
async function importKey(b64) {
  return crypto.subtle.importKey(
    "raw",
    b64ToU8(b64),
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

/**
 * Called at join/create time while we still have the plaintext PIN.
 * Derives and persists the family's private-note key. Safe to call repeatedly.
 */
export async function setupPrivateNoteKey(pin, familyId) {
  if (!pin || !familyId || !window.crypto?.subtle) return false;
  try {
    const key = await deriveKey(pin, familyId);
    const raw = await exportKey(key);
    localStorage.setItem(KEY_STORAGE_PREFIX + familyId, raw);
    return true;
  } catch {
    return false;
  }
}

export function clearPrivateNoteKey(familyId) {
  if (familyId) localStorage.removeItem(KEY_STORAGE_PREFIX + familyId);
}

/**
 * Returns true iff the current device has a derived key for the active family.
 */
export function hasPrivateNoteKey(familyId) {
  return !!familyId && !!localStorage.getItem(KEY_STORAGE_PREFIX + familyId);
}

async function activeKey(familyId) {
  const raw = localStorage.getItem(KEY_STORAGE_PREFIX + familyId);
  if (!raw) return null;
  return importKey(raw);
}

// --- encrypt / decrypt ------------------------------------------------------
const PREFIX = "enc:v1:";

/**
 * Is this string a ciphertext produced by this module?
 */
export function isEncryptedNote(s) {
  return typeof s === "string" && s.startsWith(PREFIX);
}

/**
 * Encrypt a plaintext note. Returns the ciphertext string, or `null` if
 * encryption is unavailable on this device — callers should fall back to
 * storing plaintext in that case (rare: only old browsers / file:// origins).
 */
export async function encryptNote(plaintext, familyId) {
  if (!plaintext || !familyId) return plaintext || null;
  try {
    const key = await activeKey(familyId);
    if (!key) return plaintext; // no key yet — keep plaintext; rare race
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const ct = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      key,
      new TextEncoder().encode(plaintext)
    );
    return `${PREFIX}${u8ToB64(iv)}:${u8ToB64(new Uint8Array(ct))}`;
  } catch {
    return plaintext;
  }
}

/**
 * Decrypt a ciphertext note. Returns the plaintext string on success, or
 * the literal string "🔒" (with optional reason) when the current device
 * can't decrypt (wrong key / PIN rotated / WebCrypto missing).
 *
 * Non-encrypted strings pass through unchanged, so legacy private notes
 * still display.
 */
export async function decryptNote(cipherOrPlaintext, familyId) {
  if (!cipherOrPlaintext) return cipherOrPlaintext;
  if (!isEncryptedNote(cipherOrPlaintext)) return cipherOrPlaintext;
  try {
    const body = cipherOrPlaintext.slice(PREFIX.length);
    const [ivB64, ctB64] = body.split(":");
    if (!ivB64 || !ctB64) return "🔒 Locked";
    const key = await activeKey(familyId);
    if (!key) return "🔒 Locked — re-enter family PIN to read";
    const pt = await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: b64ToU8(ivB64) },
      key,
      b64ToU8(ctB64)
    );
    return new TextDecoder().decode(pt);
  } catch {
    return "🔒 Locked";
  }
}
